/**
 * Docs › Team setup (ADR-217 with Amendments 1 and 2; contract.md "The
 * console"). Docs › Skills became this page's first tab. Four tabs, each at
 * its own address:
 *
 *  - Skills: ADR-216's list, with an "In the team setup" switch on each skill
 *    that writes the whole list (PUT /team-setup/{workspace}/skills).
 *  - Hooks & MCP: every hook command and MCP server of the latest team plugin,
 *    word for word, and each version of the two parts with its publisher, its
 *    date and the lines changed since the version before.
 *  - Instructions: the team's CLAUDE.md as a document, and its history.
 *  - Memory: each project's files with who synced each and when, and the
 *    answered values (kind, answer, role, identifier, who, when), never a
 *    phone number or an email address. For an owner or admin an identifier
 *    opens the person in a new tab of the Evrella console.
 *
 * "Set up this computer" copies the one command that installs all of it. The
 * console publishes nothing here: the parts are published by an agent over
 * MCP, and editing in the browser is not decided (ADR-217 §5).
 *
 * "What every session is told" shows, word for word, the text that reaches the
 * system prompt of every session with the SystemDox MCP server (ADR-223).
 */
import { useEffect, useMemo, useState } from 'react';
import { Navigate, NavLink, useLocation, useParams } from 'react-router-dom';
import { ExternalLink, Terminal } from 'lucide-react';
import { LoadingState } from '@systemdox/ui';
import { useTenant } from '@/contexts/TenantContext';
import { MarkdownRenderer } from '@/components/docs/MarkdownRenderer';
import { cn } from '@/lib/utils';
import {
  refusalCode,
  refusalDetail,
  teamSetupApi,
  type MemoryProjectSummary,
  type MemoryVersion,
  type Part,
  type PartDetail,
  type PartVersionFiles,
  type PluginVersion,
  type SessionBrief,
  type TeamSetup,
  type ValueAnswerListed,
} from '@/services/teamSetupApi';
import SkillsTab, { CopyButton, usePublisherNames } from './SkillsTab';
import { SkillDiff } from './SkillCompare';
import { setupCommand } from './skillInstall';
import {
  TEAM_SETUP_PATH,
  TEAM_SETUP_TABS,
  answerText,
  canOpenPeople,
  evrellaPersonUrl,
  isTeamSetupTab,
  kindText,
  when,
  type TeamSetupTab,
} from './teamSetup';

type NameOf = (userId?: string) => string;

const NOTE = 'text-sm text-slate-500 dark:text-slate-400';
const ERROR = 'text-sm text-red-600 dark:text-red-400';
const HEADING = 'mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100';
const CODE =
  'block whitespace-pre-wrap break-all rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-800 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200';

/** A part never published answers 404: no history, not an error. */
function orNull<T>(p: Promise<T>): Promise<T | null> {
  return p.catch((err) => {
    if ((err as { response?: { status?: number } })?.response?.status === 404) return null;
    throw err;
  });
}

export default function TeamSetupPage() {
  const { tab } = useParams();
  const { search } = useLocation();
  const [setup, setSetup] = useState<TeamSetup | null>(null);
  const [loadError, setLoadError] = useState(false);
  // Apart from `setup`: the Skills tab keeps its own list once it has loaded.
  const [sessionBrief, setSessionBrief] = useState<SessionBrief | undefined>();

  useEffect(() => {
    teamSetupApi
      .getOwn()
      .then((own) => {
        setSetup(own);
        setSessionBrief(own.sessionBrief);
      })
      .catch(() => setLoadError(true));
  }, []);

  /** The list changed, so the text changed. A failed read leaves the old text, not an error. */
  function refreshSessionBrief() {
    teamSetupApi
      .getOwn()
      .then((own) => setSessionBrief(own.sessionBrief))
      .catch(() => undefined);
  }

  if (!isTeamSetupTab(tab)) return <Navigate to={`${TEAM_SETUP_PATH}/skills${search}`} replace />;

  return (
    <div className="mx-auto max-w-5xl" data-testid="team-setup">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Team setup</h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-600 dark:text-slate-400">
            Your team&apos;s Claude Code setup: the skills, hooks and MCP servers every member installs, the team&apos;s
            instructions and its memory. Read every command here before you install it.
          </p>
        </div>
        <CopyButton
          label="Set up this computer"
          text={setupCommand()}
          icon={Terminal}
          primary
          testId="team-setup-command"
        />
      </div>
      <p className="mb-4 text-xs text-slate-500">
        Set up this computer copies <code className="font-mono">{setupCommand()}</code>: paste it into a terminal on
        the computer where you use Claude Code.
      </p>

      {sessionBrief && (
        <details className="mb-4 max-w-4xl text-sm" data-testid="team-setup-session-brief">
          <summary className="cursor-pointer font-medium text-slate-700 dark:text-slate-300">
            What every session is told
          </summary>
          <p className={cn(NOTE, 'mt-2')}>
            Every session on a computer with the SystemDox MCP server reads this when it starts. A skill in the team
            setup installs itself if it is plain: a name, a description and its own files, so installing it writes files
            and runs nothing. Hooks and MCP servers wait for the person, in their own terminal, and so does any other
            skill. This is what you can read; a session also learns what its own computer lacks.
          </p>
          <pre className={cn(CODE, 'mt-2')} data-testid="team-setup-session-brief-text">
            {sessionBrief.text}
          </pre>
        </details>
      )}

      <nav
        aria-label="Team setup"
        className="mb-6 flex flex-wrap gap-x-1 border-b border-slate-200 dark:border-slate-800"
        data-testid="team-setup-tabs"
      >
        {TEAM_SETUP_TABS.map((t) => (
          <NavLink
            key={t.key}
            to={`${TEAM_SETUP_PATH}/${t.key}${search}`}
            data-testid={`team-setup-tab-${t.key}`}
            className={({ isActive }) =>
              cn(
                '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
              )
            }
          >
            {t.title}
          </NavLink>
        ))}
      </nav>

      <TabBody tab={tab} setup={setup} loadError={loadError} onSkillsChanged={refreshSessionBrief} />
    </div>
  );
}

function TabBody({
  tab,
  setup,
  loadError,
  onSkillsChanged,
}: {
  tab: TeamSetupTab;
  setup: TeamSetup | null;
  loadError: boolean;
  onSkillsChanged: () => void;
}) {
  if (tab === 'skills') return <SkillsSetupTab setup={setup} onChanged={onSkillsChanged} />;
  if (loadError) return <p className={ERROR}>The team setup could not be loaded.</p>;
  if (!setup) return <LoadingState />;
  if (tab === 'hooks') return <HooksMcpTab workspace={setup.workspace} />;
  if (tab === 'instructions') return <InstructionsTab workspace={setup.workspace} />;
  return <MemoryTab workspace={setup.workspace} />;
}

/* ── Skills ── */

function SkillsSetupTab({ setup, onChanged }: { setup: TeamSetup | null; onChanged: () => void }) {
  const [names, setNames] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (setup) setNames(setup.skills);
  }, [setup]);

  async function toggle(name: string, on: boolean) {
    if (!setup || !names) return;
    const next = on ? [...names.filter((n) => n !== name), name] : names.filter((n) => n !== name);
    setBusy(true);
    setMessage(null);
    try {
      setNames(await teamSetupApi.setSkills(setup.workspace, next));
      onChanged();
      setMessage({
        ok: true,
        text: on
          ? `“${name}” is in the team setup. Every member gets it at their next update.`
          : `“${name}” is no longer in the team setup.`,
      });
    } catch (err) {
      const detail = refusalDetail(err);
      setMessage({
        ok: false,
        text:
          refusalCode(err) === 'SYD-K007'
            ? (detail ?? `SYD-K007: “${name}” is not a skill you can read.`)
            : `Could not change the team setup${detail ? `: ${detail}` : '.'}`,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {message && (
        <p
          role="status"
          data-testid="team-setup-skills-message"
          className={cn(
            'mx-auto mb-4 max-w-4xl rounded-lg border px-4 py-3 text-sm',
            message.ok
              ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200'
              : 'border-red-300 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200',
          )}
        >
          {message.text}
        </p>
      )}
      <SkillsTab
        setup={names ? { inSetup: (name) => names.includes(name), toggle: (n, on) => void toggle(n, on), busy } : undefined}
      />
    </div>
  );
}

/* ── Version history with diffs (Hooks & MCP, Instructions) ── */

function PartHistory({
  workspace,
  part,
  detail,
  nameOf,
  title,
}: {
  workspace: string;
  part: Part;
  detail: PartDetail;
  nameOf: NameOf;
  title: string;
}) {
  const [chosen, setChosen] = useState<number | null>(null);
  const [sides, setSides] = useState<{ before: PartVersionFiles | null; after: PartVersionFiles } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const previous = (v: number) => detail.versions.find((o) => o.version < v)?.version;

  useEffect(() => {
    if (chosen === null) return;
    const before = detail.versions.find((o) => o.version < chosen)?.version;
    setSides(null);
    setError(null);
    Promise.all([
      before ? teamSetupApi.getPartVersion(workspace, part, before) : Promise.resolve(null),
      teamSetupApi.getPartVersion(workspace, part, chosen),
    ])
      .then(([b, a]) => setSides({ before: b, after: a }))
      .catch(() => setError('This version is no longer kept (the newest 20 are).'));
  }, [workspace, part, chosen, detail]);

  return (
    <section className="mt-6" data-testid={`team-setup-versions-${part}`}>
      <h3 className={HEADING}>{title}</h3>
      <ul className="divide-y divide-slate-200 text-xs dark:divide-slate-800">
        {detail.versions.map((v) => (
          <li
            key={v.version}
            data-testid={`team-setup-version-${part}-${v.version}`}
            className={cn(
              'flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-1.5 text-slate-600 dark:text-slate-400',
              v.version === chosen && 'font-medium text-slate-900 dark:text-slate-100',
            )}
          >
            <button
              type="button"
              onClick={() => setChosen(v.version)}
              data-testid={`team-setup-choose-${part}-${v.version}`}
              className="hover:underline"
            >
              version {v.version}
              {v.version === detail.latestVersion ? ' (latest)' : ''}
            </button>
            <span className="ml-auto">
              {when(v.publishedAt)} by {nameOf(v.publishedBy)}
            </span>
          </li>
        ))}
      </ul>
      {chosen !== null && (
        <div className="mt-3" data-testid={`team-setup-diff-${part}`}>
          <p className="mb-2 text-xs text-slate-500">
            {previous(chosen)
              ? `What changed in version ${chosen} since version ${previous(chosen)}.`
              : `Version ${chosen} is the first kept: every line is new.`}
          </p>
          {error ? (
            <p className={ERROR}>{error}</p>
          ) : sides ? (
            <SkillDiff from={sides.before?.files ?? []} to={sides.after.files} />
          ) : (
            <LoadingState />
          )}
        </div>
      )}
    </section>
  );
}

/* ── Hooks & MCP ── */

function HooksMcpTab({ workspace }: { workspace: string }) {
  const nameOf = usePublisherNames();
  const [state, setState] = useState<{
    plugin: PluginVersion | null;
    hooks: PartDetail | null;
    mcp: PartDetail | null;
  } | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    Promise.all([
      orNull(teamSetupApi.getPlugin(workspace)),
      orNull(teamSetupApi.getPart(workspace, 'hooks')),
      orNull(teamSetupApi.getPart(workspace, 'mcp')),
    ])
      .then(([plugin, hooks, mcp]) => setState({ plugin: plugin?.versions[0] ?? null, hooks, mcp }))
      .catch(() => setError(true));
  }, [workspace]);

  if (error) return <p className={ERROR}>The hooks and MCP servers could not be loaded.</p>;
  if (!state) return <LoadingState />;
  const { plugin, hooks, mcp } = state;
  const hookCommands = plugin?.hookCommands ?? [];
  const servers = plugin?.mcpServers ?? [];

  return (
    <div data-testid="team-setup-hooks">
      <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
        Every command below runs on each member&apos;s computer, exactly as written here.
        {plugin && (
          <span className="text-slate-500">
            {' '}
            Team plugin version {plugin.version}, made {when(plugin.createdAt)}
            {plugin.createdBy ? ` by ${nameOf(plugin.createdBy)}` : ''}.
          </span>
        )}
      </p>

      <section className="mb-6">
        <h3 className={HEADING}>Hook commands</h3>
        {hookCommands.length === 0 ? (
          <p className={NOTE}>No hook is in the team setup.</p>
        ) : (
          <ul className="space-y-2" data-testid="team-setup-hook-commands">
            {hookCommands.map((command, i) => (
              <li key={i}>
                <code className={CODE} data-testid="team-setup-hook-command">
                  {command}
                </code>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mb-6">
        <h3 className={HEADING}>MCP servers</h3>
        {servers.length === 0 ? (
          <p className={NOTE}>No MCP server is in the team setup.</p>
        ) : (
          <ul className="space-y-2" data-testid="team-setup-mcp-servers">
            {servers.map((s) => (
              <li key={s.name} data-testid="team-setup-mcp-server">
                <p className="mb-1 font-mono text-xs font-medium text-slate-900 dark:text-slate-100">{s.name}</p>
                <code className={CODE} data-testid="team-setup-mcp-command">
                  {s.url ?? [s.command ?? '', ...(s.args ?? [])].join(' ')}
                </code>
              </li>
            ))}
          </ul>
        )}
      </section>

      {hooks && <PartHistory workspace={workspace} part="hooks" detail={hooks} nameOf={nameOf} title="Hooks versions" />}
      {mcp && <PartHistory workspace={workspace} part="mcp" detail={mcp} nameOf={nameOf} title="MCP versions" />}
    </div>
  );
}

/* ── Instructions ── */

function InstructionsTab({ workspace }: { workspace: string }) {
  const nameOf = usePublisherNames();
  const [state, setState] = useState<{ detail: PartDetail; latest: PartVersionFiles } | 'none' | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    orNull(teamSetupApi.getPart(workspace, 'instructions'))
      .then(async (detail) => {
        if (!detail) return setState('none');
        const latest = await teamSetupApi.getPartVersion(workspace, 'instructions', 'latest');
        setState({ detail, latest });
      })
      .catch(() => setError(true));
  }, [workspace]);

  if (error) return <p className={ERROR}>The instructions could not be loaded.</p>;
  if (!state) return <LoadingState />;
  if (state === 'none') return <p className={NOTE}>No instructions are published yet.</p>;
  const claudeMd = state.latest.files.find((f) => f.path === 'CLAUDE.md') ?? state.latest.files[0];

  return (
    <div data-testid="team-setup-instructions">
      <p className="mb-3 text-xs text-slate-500">
        CLAUDE.md, version {state.latest.version} · published {when(state.latest.publishedAt)} by{' '}
        {nameOf(state.latest.publishedBy)}
      </p>
      <div
        className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900"
        data-testid="team-setup-instructions-document"
      >
        <MarkdownRenderer content={claudeMd?.content ?? ''} />
      </div>
      <PartHistory workspace={workspace} part="instructions" detail={state.detail} nameOf={nameOf} title="Versions" />
    </div>
  );
}

/* ── Memory ── */

function MemoryTab({ workspace }: { workspace: string }) {
  // No address on this tab: a member with no name is "a member".
  const nameOf = usePublisherNames({ addresses: false });
  const { tenant } = useTenant();
  const [projects, setProjects] = useState<{ summary: MemoryProjectSummary; latest: MemoryVersion | null }[] | null>(
    null,
  );
  const [answers, setAnswers] = useState<ValueAnswerListed[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    teamSetupApi
      .listMemoryProjects(workspace)
      .then((list) =>
        Promise.all(
          list.map(async (summary) => ({
            summary,
            latest: await orNull(teamSetupApi.getMemory(workspace, summary.project)),
          })),
        ),
      )
      .then(setProjects)
      .catch(() => setError('The team memory could not be loaded.'));
    teamSetupApi
      .listValueAnswers(workspace)
      .then(setAnswers)
      .catch(() => setAnswers([]));
  }, [workspace]);

  const orgId = tenant?.tenantId ?? null;
  const open = canOpenPeople(tenant?.role) && Boolean(orgId);

  if (error) return <p className={ERROR}>{error}</p>;
  if (!projects || !answers) return <LoadingState />;

  return (
    <div data-testid="team-setup-memory">
      {projects.length === 0 ? (
        <p className={NOTE}>No team memory is synced yet.</p>
      ) : (
        projects.map(({ summary, latest }) => (
          <MemoryProject key={summary.project} summary={summary} latest={latest} nameOf={nameOf} />
        ))
      )}

      <section className="mt-8" data-testid="team-setup-values">
        <h3 className={HEADING}>People and values answered</h3>
        <p className="mb-3 text-xs text-slate-500">
          A phone number or email address in the memory is kept as a role and an identifier. The values themselves are
          never stored or shown.
        </p>
        {answers.length === 0 ? (
          <p className={NOTE}>No value is answered yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-slate-500">
                <tr>
                  <th className="py-1 pr-3 font-medium">Kind</th>
                  <th className="py-1 pr-3 font-medium">Answer</th>
                  <th className="py-1 pr-3 font-medium">Role</th>
                  <th className="py-1 pr-3 font-medium">Identifier</th>
                  <th className="py-1 pr-3 font-medium">Answered by</th>
                  <th className="py-1 font-medium">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 text-slate-700 dark:divide-slate-800 dark:text-slate-300">
                {answers.map((a, i) => (
                  <tr key={i} data-testid="team-setup-value">
                    <td className="py-1.5 pr-3">{kindText(a.kind)}</td>
                    <td className="py-1.5 pr-3">{answerText(a.answer)}</td>
                    <td className="py-1.5 pr-3">{a.label ?? ''}</td>
                    <td className="py-1.5 pr-3 font-mono">
                      {a.personId ? (
                        <Identifier personId={a.personId} href={open && orgId ? evrellaPersonUrl(orgId, a.personId) : null} />
                      ) : (
                        ''
                      )}
                    </td>
                    <td className="py-1.5 pr-3">{nameOf(a.answeredBy)}</td>
                    <td className="py-1.5">{when(a.answeredAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Identifier({ personId, href }: { personId: string; href: string | null }) {
  if (!href) return <span data-testid="team-setup-person">{personId}</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="team-setup-person"
      className="inline-flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
    >
      {personId}
      <ExternalLink className="h-3 w-3" aria-hidden />
    </a>
  );
}

function MemoryProject({
  summary,
  latest,
  nameOf,
}: {
  summary: MemoryProjectSummary;
  latest: MemoryVersion | null;
  nameOf: NameOf;
}) {
  const files = useMemo(
    () => Object.entries(latest?.files ?? {}).sort(([a], [b]) => a.localeCompare(b)),
    [latest],
  );
  return (
    <section className="mb-6" data-testid="team-setup-memory-project" data-project={summary.project}>
      <h3 className={HEADING}>
        <span className="font-mono">{summary.project}</span>{' '}
        <span className="font-normal text-slate-500">
          · version {latest?.version ?? summary.latestVersion} · {files.length || summary.fileCount} file(s)
        </span>
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="text-slate-500">
            <tr>
              <th className="py-1 pr-3 font-medium">File</th>
              <th className="py-1 pr-3 font-medium">Synced by</th>
              <th className="py-1 font-medium">When</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 text-slate-700 dark:divide-slate-800 dark:text-slate-300">
            {files.map(([path, f]) => (
              <tr key={path} data-testid="team-setup-memory-file">
                <td className="py-1.5 pr-3 font-mono">{path}</td>
                <td className="py-1.5 pr-3">{nameOf(f.syncedBy)}</td>
                <td className="py-1.5">{when(f.syncedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
